import {
  AnalyticsInsightMetadata,
  AnalyticsItemMetadata,
  ParsedBarPoint,
  ParsedItemCell,
  ParsedTable,
  PostHogInsightResponse,
} from "./types";

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const firstNumeric = (values: unknown[]): number | null => {
  for (const value of values) {
    const num = toNumber(value);
    if (num !== null) return num;
  }
  return null;
};

const sumNumericArray = (values: unknown[]): number | null => {
  let total = 0;
  let seen = false;

  for (const value of values) {
    const num = toNumber(value);
    if (num !== null) {
      total += num;
      seen = true;
    }
  }

  return seen ? total : null;
};

const formatBreakdownLabel = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  if (Array.isArray(value)) {
    return value.length ? String(value[0]) : null;
  }
  return String(value);
};

const asSeriesRecords = (
  results: unknown,
): Record<string, unknown>[] => {
  if (!Array.isArray(results)) return [];
  return results.filter(
    (row): row is Record<string, unknown> =>
      !!row && typeof row === "object" && !Array.isArray(row),
  );
};

export function normalizeInsightResponse(
  response: PostHogInsightResponse | undefined,
): PostHogInsightResponse | undefined {
  if (!response) return undefined;

  const normalized: PostHogInsightResponse = { ...response };
  const payload = normalized.results ?? normalized.result;

  if (payload && typeof payload === "object" && !Array.isArray(payload)) {
    const nested = payload as Record<string, unknown>;
    if (Array.isArray(nested.results) || Array.isArray(nested.rows)) {
      normalized.results = nested.results ?? nested.rows;
      normalized.columns =
        (nested.columns as string[] | undefined) ?? normalized.columns;
    }
  } else if (payload !== undefined) {
    normalized.results = payload;
  }

  return normalized;
}

export const extractSeriesTotal = (
  row: Record<string, unknown>,
): number | null => {
  // `aggregated_value` is the canonical total PostHog reports for BoldNumber
  // and table/breakdown insights. Prefer it over the per-day `data`/`count`
  // fields, which are commonly empty (`[]`) or `0` for those insight types.
  const aggregated = toNumber(row.aggregated_value);
  if (aggregated !== null) return aggregated;

  const data = row.data;
  if (Array.isArray(data)) {
    const sum = sumNumericArray(data);
    if (sum !== null) return sum;
  }

  return (
    toNumber(row.count) ??
    toNumber(row.value) ??
    firstNumeric(Object.values(row))
  );
};

// PostHog buckets overflow / missing breakdown values into sentinel strings.
// Surface them as readable labels. `nullLabel` controls the "no value" bucket:
// pass a label (e.g. "No Department") to keep + rename it, or omit to drop it.
const cleanBreakdownLabel = (
  value: string | null,
  nullLabel: string | null = null,
): string | null => {
  if (value == null) return null;
  if (value.includes("_posthog_breakdown_other_")) return "Other";
  if (value.includes("_posthog_breakdown_null_")) return nullLabel;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
};

// Map a raw PostHog event/action name to a friendly metric column header.
const friendlyMetricName = (raw: unknown): string | null => {
  if (typeof raw !== "string" || !raw.trim()) return null;
  const key = raw.toLowerCase();
  if (key.includes("click")) return "Clicks";
  if (key.includes("view")) return "Views";
  if (
    key.includes("save") ||
    key.includes("product_added") ||
    key.includes("added")
  ) {
    return "Saves";
  }
  return null;
};

const extractItemsMetadata = (
  response?: PostHogInsightResponse,
): Record<string, AnalyticsItemMetadata> | undefined => {
  const metadata = response?.metadata as AnalyticsInsightMetadata | undefined;
  if (!metadata?.items || typeof metadata.items !== "object") return undefined;
  return metadata.items;
};

const resolveItemCell = (
  itemId: string,
  itemsMeta?: Record<string, AnalyticsItemMetadata>,
): ParsedItemCell | null => {
  if (!itemsMeta || itemId === "Other") return null;

  const meta = itemsMeta[itemId];
  if (!meta) return null;

  const name = meta.item_name?.trim() || itemId;
  return {
    id: itemId,
    name,
    imageSrc: meta.item_image_src ?? null,
  };
};

const formatMetricValue = (metric: string, total: number): string => {
  if (metric.toLowerCase().includes("ctr") || metric.includes("%")) {
    return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(total)}%`;
  }
  return String(total);
};

const metricLabelFromSeries = (row: Record<string, unknown>): string => {
  const customName = row.custom_name;
  if (typeof customName === "string" && customName.trim()) {
    return customName.trim();
  }

  const breakdown = formatBreakdownLabel(row.breakdown_value);
  const label = typeof row.label === "string" ? row.label : "";

  // For breakdown insights the series `label` IS the breakdown value (e.g. the
  // item name), so it can't double as the metric/column name. Derive the
  // metric from the underlying event/action instead — otherwise every row
  // would create its own column.
  if (breakdown && (!label || label.trim() === breakdown.trim())) {
    const action = row.action as Record<string, unknown> | null | undefined;
    const actionName =
      action && typeof action === "object"
        ? action.custom_name ?? action.name ?? action.id
        : null;
    return friendlyMetricName(actionName) ?? "Total";
  }

  if (!label) return "Value";

  const parts = label.split(" - ");
  return parts[parts.length - 1]?.trim() || label;
};

const parseTrendsBreakdownTable = (
  seriesRows: Record<string, unknown>[],
  response?: PostHogInsightResponse,
): ParsedTable | null => {
  const itemsMeta = extractItemsMetadata(response);
  const grouped = new Map<
    string,
    { displayName: string; metrics: Record<string, string> }
  >();

  for (const row of seriesRows) {
    const itemId =
      cleanBreakdownLabel(formatBreakdownLabel(row.breakdown_value)) ??
      cleanBreakdownLabel(formatBreakdownLabel(row.item_name)) ??
      cleanBreakdownLabel(formatBreakdownLabel(row.category_name));
    const total = extractSeriesTotal(row);
    if (!itemId || total === null) continue;

    const metric = metricLabelFromSeries(row);
    const itemCell = resolveItemCell(itemId, itemsMeta);
    const displayName = itemCell?.name ?? itemId;
    const existing = grouped.get(itemId) ?? {
      displayName,
      metrics: {},
    };
    existing.metrics[metric] = formatMetricValue(metric, total);
    grouped.set(itemId, existing);
  }

  if (grouped.size === 0) return null;

  const metricHeaders = Array.from(
    new Set(
      Array.from(grouped.values()).flatMap((row) => Object.keys(row.metrics)),
    ),
  );

  const buildRows = () => {
    const rows: string[][] = [];
    const itemMetaByRow: Array<ParsedItemCell | null> = [];

    Array.from(grouped.entries()).forEach(([itemId, row]) => {
      rows.push([
        row.displayName,
        ...metricHeaders.map((header) => row.metrics[header] ?? "0"),
      ]);
      itemMetaByRow.push(resolveItemCell(itemId, itemsMeta));
    });

    return { rows, itemMetaByRow };
  };

  if (metricHeaders.length <= 1) {
    const metric = metricHeaders[0] ?? "Total";
    const { rows, itemMetaByRow } = buildRows();
    return {
      headers: ["Item", metric],
      rows,
      ...(itemsMeta ? { itemMetaByRow } : {}),
    };
  }

  const { rows, itemMetaByRow } = buildRows();
  return {
    headers: ["Item", ...metricHeaders],
    rows,
    ...(itemsMeta ? { itemMetaByRow } : {}),
  };
};

export function parseBoldNumber(
  response: PostHogInsightResponse | undefined,
): number | null {
  const normalized = normalizeInsightResponse(response);
  if (!normalized) return null;

  const direct = toNumber(normalized.result);
  if (direct !== null) return direct;

  const results = normalized.results;
  if (typeof results === "number") return results;

  if (Array.isArray(results)) {
    if (results.length === 0) return null;

    if (results.length === 1 && Array.isArray(results[0])) {
      return firstNumeric(results[0] as unknown[]);
    }

    const seriesRows = asSeriesRecords(results);
    if (seriesRows.length) {
      const formulaSeries = [...seriesRows]
        .reverse()
        .find((row) => {
          const label = String(row.label ?? row.custom_name ?? "");
          return (
            label.includes("+") ||
            label.includes("%") ||
            label.toLowerCase().includes("rate")
          );
        });

      if (formulaSeries) {
        const formulaTotal = extractSeriesTotal(formulaSeries);
        if (formulaTotal !== null) return formulaTotal;
      }

      const lastTotal = extractSeriesTotal(seriesRows[seriesRows.length - 1]);
      if (lastTotal !== null) return lastTotal;
    }

    const last = results[results.length - 1];
    if (typeof last === "number") return last;

    if (Array.isArray(last)) {
      return firstNumeric(last.slice(1)) ?? firstNumeric(last);
    }

    if (results.every((value) => typeof value === "number")) {
      return sumNumericArray(results);
    }

    return firstNumeric(results as unknown[]);
  }

  if (results && typeof results === "object") {
    return firstNumeric(Object.values(results as Record<string, unknown>));
  }

  return null;
}

export function parsePercent(
  response: PostHogInsightResponse | undefined,
): number | null {
  const value = parseBoldNumber(response);
  if (value === null) return null;
  if (value <= 1 && value >= 0) return value * 100;
  return value;
}

const parseHogqlTable = (
  normalized: PostHogInsightResponse,
): ParsedTable | null => {
  const results = normalized.results;
  const columns = normalized.columns ?? [];

  if (!Array.isArray(results) || results.length === 0) {
    return null;
  }

  const rows: string[][] = results.map((row) => {
    if (Array.isArray(row)) {
      return row.map((cell) => (cell == null ? "" : String(cell)));
    }
    if (row && typeof row === "object") {
      const record = row as Record<string, unknown>;
      if (columns.length) {
        return columns.map((col) => {
          const val = record[col];
          return val == null ? "" : String(val);
        });
      }

      return Object.values(record).map((val) =>
        val == null ? "" : String(val),
      );
    }
    return [String(row)];
  });

  const headers =
    columns.length > 0
      ? columns.map(String)
      : rows[0]?.map((_, index) => `Column ${index + 1}`) ?? [];

  return { headers, rows };
};

export function parseTable(
  response: PostHogInsightResponse | undefined,
): ParsedTable | null {
  const normalized = normalizeInsightResponse(response);
  if (!normalized) return null;

  const seriesRows = asSeriesRecords(normalized.results);
  const trendsTable = parseTrendsBreakdownTable(seriesRows, normalized);
  if (trendsTable) return trendsTable;

  return parseHogqlTable(normalized);
}

export function parseBarBreakdown(
  response: PostHogInsightResponse | undefined,
): ParsedBarPoint[] | null {
  const normalized = normalizeInsightResponse(response);
  const results = normalized?.results;

  if (!Array.isArray(results) || results.length === 0) return null;

  const points: ParsedBarPoint[] = [];

  // Keep the "no breakdown value" bucket on the chart, labelled clearly.
  const NULL_LABEL = "No Department";

  for (const row of results) {
    if (Array.isArray(row) && row.length >= 2) {
      const label = cleanBreakdownLabel(String(row[0] ?? ""), NULL_LABEL);
      const value = toNumber(row[1]);
      if (label && value !== null) points.push({ label, value });
      continue;
    }

    if (row && typeof row === "object") {
      const record = row as Record<string, unknown>;
      const label =
        cleanBreakdownLabel(
          formatBreakdownLabel(record.breakdown_value),
          NULL_LABEL,
        ) ??
        cleanBreakdownLabel(
          formatBreakdownLabel(record.category_name),
          NULL_LABEL,
        ) ??
        cleanBreakdownLabel(formatBreakdownLabel(record.label), NULL_LABEL) ??
        cleanBreakdownLabel(formatBreakdownLabel(record.name), NULL_LABEL);
      const value = extractSeriesTotal(record);

      if (label && value !== null) {
        points.push({ label, value });
      }
    }
  }

  return points.length ? points : null;
}

export function formatAnalyticsNumber(value: number | null): string {
  if (value === null) return "—";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(
    value,
  );
}

export function formatAnalyticsPercent(value: number | null): string {
  if (value === null) return "—";
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(value)}%`;
}
